import {
  afterRender,
  ApplicationRef,
  type ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  Injector,
} from '@angular/core';

import { IconsContainerComponent } from './icons-container.component';

@Injectable({ providedIn: 'root' })
export class IconsContainerService {
  private readonly injector = inject(Injector);
  private readonly applicationRef = inject(ApplicationRef);
  private readonly envInjector = inject(EnvironmentInjector);

  private containers = new WeakMap<Node, ComponentRef<IconsContainerComponent>>();

  public async getContainer(iconEl: HTMLElement, injector: Injector) {
    const root = await this.getIconRootNode(iconEl, injector);
    const container = this.containers.get(root);
    if (container) return container;

    const componentRef = createComponent(IconsContainerComponent, {
      elementInjector: this.injector,
      environmentInjector: this.envInjector,
    });
    this.applicationRef.attachView(componentRef.hostView);
    this.insertContainer(root, componentRef.location.nativeElement);
    this.containers.set(root, componentRef);
    return this.containers.get(root)!;
  }

  private getIconRootNode(iconEl: HTMLElement, injector: Injector) {
    return new Promise<ParentNode>((resolve, reject) => {
      if (iconEl.isConnected) return resolve(<ParentNode>iconEl.getRootNode());

      const ref = afterRender(
        () => {
          if (!iconEl.isConnected) return;

          const root = <ParentNode>iconEl.getRootNode();
          ref.destroy();
          resolve(root);
        },
        { injector },
      );
    });
  }

  private insertContainer(root: ParentNode, containerEl: HTMLElement) {
    if (root instanceof Document) {
      root.body.prepend(containerEl);
    } else {
      root.prepend(containerEl);
    }
  }
}
